/**
 * @typedef ConducibilitaPageProps
 * @prop {string} idPiezometro
 */

import { useEffect } from "react";
import { log } from "../../../../common/functions/log";
import { piezometriService } from "../../../../services/PiezometriService";
import { Table } from "../../components/micro/Table";
import { Tr } from "../../components/micro/Tr";
import { Fragment, useState } from "react";
import { downloadExcel } from "../../../../common/functions/downloadExcel";
import { DropdownMenu } from "../../../../common/components/DropdownMenu";
import { SearchElement, SearchForm, SearchLabel, SearchRow } from "../../../../common/components/Search/Search";

/**
 * @typedef MeasurementSearchParams
 * @prop {Date} from
 * @prop {Date} to
 */

/**
 * @typedef SamplesTableProps
 * @prop {Array<MeasureGroup>} samples
 * @prop {ReactNode | undefined} title
 */

/**
 * @typedef Measure
 * @prop {Date} date
 * @prop {string} param
 * @prop {string} value
 */

/**
 * @typedef MeasureGroup
 * @prop {Date} date
 * @prop {Measure[]} measures
 */

const api = {
    /**
     *
     * @param {string} idPiezometro
     * @param {MeasurementSearchParams} params
     * @returns
     */
    search: async (idPiezometro, params) => {
        console.log({params})
        const data = await piezometriService.getAcque(
            idPiezometro,
            params.from,
            params.to
        )

        return data
    }
}

export function AnalisiPage({idPiezometro, nomePiezometro}) {


    const [lastMeasurement, setLastMeasurement] = useState(null)
    useEffect(() => {
        piezometriService.lastAcque(idPiezometro)
            .then(setLastMeasurement)
    }, [idPiezometro])

    /**
     * @type {[Measure[], (m: Measure[]) => void]}
     */
    const [filteredData, setFilteredData] = useState([])
    const [loading, setLoading] = useState(false)

    const onSubmit = (e) => {
        e.preventDefault()
        switch (getAction(e)) {
            case 'search':
                setLoading(true)
                api.search(idPiezometro, getSearchParams(e))
                    .then(log('DATA'))
                    .then(setFilteredData)
                    .then(() => setLoading(false))
                    .then(() => setSearched(true))
                break
            case 'clear':
                e.target.reset()
                setSearched(false)
                setFilteredData([])
                break
            default: console.warn('unknown action:', getAction(e))
        }
    }

    const getAction = e => e.nativeEvent.submitter.name

    /**
     * @param {FormEvent} e
     * @returns {MeasurementSearchParams}
     */
    const getSearchParams = e => {
        const rawParams = Object.fromEntries(
            ['from', 'to'].map(el => [el, e.target.elements[el].value])
        )

        return {
            from: new Date(rawParams.from === '' ? 0 : rawParams.from),
            to: new Date(rawParams.to === '' ? '3000-12-31' : rawParams.to)
        }

    }

    const onExport = e => {
        e.preventDefault()
        xlsxExport(`Piezometro ${nomePiezometro} - analisi lab. acque sotterranee`, filteredData)
    }

    const [searched, setSearched] = useState(false)

    return (
        <>
            <SamplesTable
                title={<th colSpan='100%'>Ultima misura{lastMeasurement ? '' : ' non disponibile'}</th>}
                samples={lastMeasurement ? [lastMeasurement] : []}
            />

            <SearchForm className="p-3" onSubmit={onSubmit}>
                <SearchRow>
                    <SearchElement>
                        <SearchLabel>Da:&nbsp;</SearchLabel>
                        <input id="from" name="from" type="date"/>
                    </SearchElement>

                    <SearchElement>
                        <SearchLabel>A:&nbsp;</SearchLabel>
                        <input id="to" name="to" type="date"/>
                    </SearchElement>

                    <div className="d-flex align-items-end justify-content-end">
                        <button type="submit" name="search" id="search-btn" className="btn btn-sm"><i className="fa fa-search text-white"></i></button>
                        <button type="submit" name="clear" id="clear-btn" className="btn btn-sm"><i className="fa fa-eraser text-white"></i></button>
                        <DropdownMenu
                            className="text-dark"
                            trigger={<i className="fa fa-bars text-white"></i>}
                            items={[
                                {text: 'Esporta', onClick: onExport}
                            ]}
                        />
                    </div>
                </SearchRow>
            </SearchForm>

            {loading
                ? <div className="text-center my-5">RICERCA IN CORSO...</div>
                : <SamplesTable
                    samples={filteredData ?? []}
                    noResults={searched
                        ? 'Nessun risultato'
                        : 'Effettua una ricerca per visualizzare dei dati'
                    }
                  />
            }
        </>
    )
}

/**
 * @typedef {SamplesTableProps}
 * @prop {Measure[]} samples
 * @prop {ReactNode | undefined} title
 * @prop {ReactNode | undefined} noResults
 */

/**
 * @param {SamplesTableProps} param0
 * @returns
 */
function SamplesTable({
    samples,
    title,
    noResults
}) {
    return (
        <Table>
            <thead>
                {title && <Tr className="title">{title}</Tr>}
                <Tr>
                    <th>Parametro [udm]</th>
                    <th>Valore</th>
                </Tr>
            </thead>

            <tbody>
                {samples.length === 0 && noResults &&
                    <Tr><td colSpan={2}>{noResults}</td></Tr>
                }
                {samples.map((sample) =>
                    <Fragment key={sample.date.toLocaleDateString()}>
                        <tr className="special" key={sample.date + ''}>
                            <td colSpan="100%">{sample.date.toLocaleDateString()}</td>
                        </tr>
                        {sample.measures.map((measure, i) =>
                            <Tr key={measure.param}>
                                <td>{measure.param}</td>
                                <td>{measure.value}</td>
                            </Tr>
                        )}
                    </Fragment>
                )}
            </tbody>
        </Table>
    )
}

/**
 * @param {string} title
 * @param {MeasureGroup[]} samples
 */
const xlsxExport = (title, samples) => {

    const header = [
        'Data',
        ...samples[0].measures.map(m => m.param)
    ]

    const body = samples.map(group => [
        group.date.toLocaleDateString(),
        ...group.measures.map(sample => sample.value)
    ])

    const data = [header, ...body]

    downloadExcel(title, data);
}